import { reactive } from "@vue/composition-api";
import { deepClone, omit, toType } from "@/helpers/helpers";
import { filters as availableFilters, TABLE_OPTIONS_KEYS } from "@/components/NewFilterBar/filters";
import { state as appState } from "@/store/modules/app";
import { state as bookmarkState } from "@/store/modules/bookmark";
import { Router } from "@/composables/Router";


const decorateAvailableFilters = input => {
    /**
     *  Use this function if you want to override any keys in the available filters at consumption-time.
     *  Useful, for example, if you want to replace available items with a getter function or a ref.
     *
     *  Input must be an array of objects with the following interface:
     *  {
     *    key: <string>,
     *    property: <string>,
     *    value: <any | ref>
     *  }
     */
    const availableFiltersClone = deepClone(availableFilters);

    if (input) {

        input.map(i => {
            const index = availableFiltersClone.findIndex(ii => ii.key === i.key);
            availableFiltersClone[index][i.property] = i.value;
        });
    }

    return availableFiltersClone;
};


export const getFilterHelper = function (
    {
        filters = [],
        table_parameters = [],
        custom_parameters = [],
        bookmarkType = null
    }) {

    /**
     * This interface should be used to:
     *      make sure default filters are always in url
     *      manage filter and data table related url changes
     *      reset filters to initial state
     */
    return reactive({
        /**
         * Fill out default parameters in url
         * (only missing parameters)
         */

        setFilterParamsIfEmpty: async function() {
            await this.loadDefaultBookmark()

            let params = convertQueryStringToParamsObject(getFullQuery())

            let default_params_object = this.getAsKeyValueObject([...this.getDefaultFilters(), ...table_parameters, ...custom_parameters])

            if (!params || !Object.keys(params).length){
                return setUrlParametersFromObject(default_params_object, false, true)
            }

            let missing_required_params;

            missing_required_params = omit(default_params_object, Object.keys(params))
            if ((missing_required_params && Object.keys(missing_required_params).length)){
                return setUrlParametersFromObject(missing_required_params)
            }
        },

        loadDefaultBookmark: async function() {
            if (!bookmarkType) {
                return
            }

            let params = convertQueryStringToParamsObject(getFullQuery())
            if (Object.keys(params).length) {
                return
            }

            const defaultBookmarksJson = localStorage.getItem('defaultBookmarks')
            if (!defaultBookmarksJson) {
                return
            }

            let defaultBookmarks = {}

            try {
                defaultBookmarks = JSON.parse(defaultBookmarksJson)
            } catch (e) {
                console.error(e)
                return
            }

            const defaultBookmarkId = defaultBookmarks[bookmarkType]
            if (!defaultBookmarkId) {
                return
            }

            const bookmark = bookmarkState.list.find(x => x.id === defaultBookmarkId)
            if (!bookmark) {
                return
            }

            const router = Router();

            let new_link = bookmark.link.replace(/^\/app/, "");
            new_link = new_link.replaceAll('active_bookmark_id', 'x');
            new_link += "&active_bookmark_id=" + bookmark.id

            await router.push(new_link)
        },

        getAsKeyValueObject: function(object){
            return object.reduce((a,b) => {
                return { ...a, [b['key']]: b['value'] }
            }, {})
        },

        /**
         * Returns the current query as parameters, not reactive
         */
        queryParameters: function(){
            return convertQueryStringToParamsObject(getFullQuery())
        },

        /**
         *  Overwrite some filter values and filter out unused filters.
         */
        getAvailableFilters: function() {
                let overwrite_keys = ['items', 'default', 'value', 'extraItems', 'label', 'loading']
                let filter_overrides = []
                for (let key of overwrite_keys){
                    filter_overrides = filters.reduce((a,b) => {
                        if (Object.prototype.hasOwnProperty.call(b, key) && b[key]){
                            a.push({ key: b.key, property: key, value: b[key] })
                        }
                        return a
                    }, filter_overrides)
                }
                let filter_objects = decorateAvailableFilters(filter_overrides)

                filter_objects.sort((filter_a, filter_b) => {
                    let filter_a_index = filters.findIndex(f => f.key === filter_a.key)
                    let filter_b_index = filters.findIndex(f => f.key === filter_b.key)
                    return filter_a_index - filter_b_index
                })

                let f = this.getAsKeyValueObject(filters)
                filter_objects = filter_objects.filter((filter_obj) => {
                    return Object.prototype.hasOwnProperty.call(f, filter_obj.key)
                })
                return filter_objects
        },

        /**
         *   Return only v-data-table related parameters
         *   Use this as v-data-table options.
         */
        getTableOptions: function () {
            // return table option values from url
            let ignore_keys = this.getAsKeyValueObject([...filters, ...custom_parameters])
            let params = this.getOnlyRelevantQueryParams()
            let result = omit(params, Object.keys(ignore_keys))
            return result
        },

        /**
         *   Set a URL value
         */
        setUrlParam: function (key, value) {
            setUrlParameter(key, value)
        },

        /**
        *   Delete a URL value
        */
        deleteUrlParam: function (key) {
            deleteUrlParameter(key)
        },

        /**
         * Return an object of default types
         */
         getDefaultTypesObject: function(params_array){
             return params_array.reduce((a, b) => {
                return { ...a, [b.key]: getTypeDefault(b.value) }
            }, {})
        },

        /**
         *   Get a URL value
         */
        getUrlParam: function (key) {
            let val = getUrlParameter(key)

            let default_types = this.getDefaultTypesObject([
                ...filters,
                ...table_parameters,
                ...custom_parameters
            ])

            if (val === undefined) {
                return default_types[key]
            } else {
                return val
            }
        },

        /**
         *   Reset default filters to original state
         *   Remove non-default filters
         *   Do not touch table+other params
         */
        resetFilters: function () {
            let current_params = convertQueryStringToParamsObject(getFullQuery())

            let required_non_filter_parameters = this.getAsKeyValueObject([...table_parameters, ...custom_parameters])

            let missing_required_non_filter_params = required_non_filter_parameters
            let existing_non_filter_params;

            if (current_params){
                existing_non_filter_params = omit(current_params, Object.keys(this.getAsKeyValueObject(filters)))
                missing_required_non_filter_params = omit(required_non_filter_parameters, Object.keys(existing_non_filter_params))
            }

            setUrlParametersFromObject({
                ...this.getAsKeyValueObject(this.getDefaultFilters()),
                ...missing_required_non_filter_params,
                ...existing_non_filter_params },
                true
            )

        },

        // Perform changes in filters based on one filter
        resetFiltersOnAccountChange: function(filter) {
            if (['account_id', 'account_id_list'].includes(filter.key)) {
                this.resetFilters()
            } 
        },
        /**
         * Reset one filter by key
         */
        resetFilter: function(key) {
            let filter = this.getDefaultFilters().filter(x => x.key === key);
            if (filter) {
                setUrlParametersFromObject(this.getAsKeyValueObject(filter), false);
            }
        },

        /**
         * Set filters specified in params dictionary
         */
        setFilters: function(params) {
            setUrlParametersFromObject(params, false)
        },

        /**
         * Specifically update only table options, skip the first update, because of v-data-table reasons
         */
        timesTableOptsUpdated: 0,
        setTableOptions: function (e) {
            if (this.timesTableOptsUpdated < 1) {
                this.timesTableOptsUpdated++;
                return;
            }
            setUrlParametersFromObject(e)
        },

        /**
         * Change sort
         */
        changeSort: function (field_name) {
            let sortDesc = this.getUrlParam('sortBy') === field_name ? !this.getUrlParam('sortDesc') : false
            this.setUrlParam("sortDesc", sortDesc)
            this.setUrlParam("sortBy", field_name)
        },

        /**
         * Specifically set column values
         */
        setColumns: function (val) {
            if (!['array', 'string'].includes(toType(val))){
                console.error('Invalid argument type for setColumns')
            }
            this.setUrlParam('columns', val)
        },

        getDefaultFilters: function (){
            return filters.filter(filter => filter.default)
        },
        getExtraFilters: function (){
            return filters.filter(filter => !filter.default)
        },

        /**
         * Return only keys that are defined as parameters of this filterHelper
         * Use this for backend queries
         */
        getOnlyRelevantQueryParams: function(){
            let params = this.queryParameters()

            let available_keys = this.getKeys([...filters, ...table_parameters, ...custom_parameters])
            let current_keys = Object.keys(params)
            let delete_keys = current_keys.filter(key => !available_keys.includes(key))

            return omit(params, delete_keys)
        },

        getKeys: function(params){
            let filter_params = this.getAsKeyValueObject(params)
            return Object.keys(filter_params)
        },
    })
}

export const setUrlParametersFromObject = (params_object, overwrite, replaceRoute) => {
    let params = convertQueryStringToParamsObject(getFullQuery()) || {}
    if (overwrite) params = {}

    if (params_object) {
        for (let _key of Object.keys(params_object)) {
            params[_key] = params_object[_key]
        }
    }
    return _actuallySetUrlFromObject(params, replaceRoute)
}
export const setUrlParameter = (key, value) => {
    let params = convertQueryStringToParamsObject(getFullQuery())
    if (params) {
        params[key] = value
        return _actuallySetUrlFromObject(params)
    }
}
export const getUrlParameter = (key) => {
    let params = convertQueryStringToParamsObject(getFullQuery())
    if (params) {
        return params[key]
    }
}
export const deleteUrlParameter = (key) => {
    let params = convertQueryStringToParamsObject(getFullQuery())
    if (params) {
        delete params[key]
        _actuallySetUrlFromObject(params)
    }
}
const _actuallySetUrlFromObject = (params, replaceRoute) => {
    const router = Router();
    let newUrl = convertParamsObjectToQueryString(params)
    if (newUrl) {
        if (replaceRoute) {
            router.replace({ query: deepClone(params) }).catch(() => {});
        } else {
            router.push({ query: deepClone(params) }).catch(() => {});
        }
    }
    return params
}

export const getFullQuery = () => {
    /**
     * Get the full query
     *
     * Uses current Vue instance to get the router object if possible
     * Otherwise computed properties wont be able to react to changes
     */

    let fullPath;

    if (appState && appState.vueContext) {
        fullPath = appState.vueContext.root.$route.fullPath
    } else {
        const router = Router();
        fullPath = router.currentRoute.fullPath
    }
    let answer = ""
    const result = fullPath && fullPath.length ? fullPath.split("?") : []
    if (result.length >= 2) answer = result[1]
    return answer
}

export const convertParamsObjectToQueryString = (params, leading_string = '?') => {
    /**
     * This function holds the knowledge of how to convert the parameters object into a query string.
     * This is a reverse function of convertQueryStringToParamsObject.
     */

    let str = leading_string;

    Object.entries(params).map((i, index, arr) => {
        str += i[0];
        str += "=";
        const matchingFilterObject = availableFilters.find(ii => ii.key === i[0]);
        if (matchingFilterObject && matchingFilterObject.multi && i[1] && Array.isArray(i[1])) {
            str += i[1].join(matchingFilterObject.delimiter || "~");
        } else {
            str += i[1];
        }

        if (index !== arr.length - 1) {
            str += "&";
        }
    });
    // Tornado treats ; as a reserved symbol and will do magic if not escaped
    str = str.replace(';', '%3B').replace('+', '%2B');
    if (str === '?') return ''
    return str;
}

export const convertQueryStringToParamsObject = (qs) => {

    /**
     * This function holds the knowledge of how to convert the query string into a parameters object.
     * This is a reverse function of convertParamsObjectToQueryString but holds more logic since we need to infer types.
     */

    let qq = qs.split("&").reduce((acc, curr) => {
        const pair = curr.split("=");
        if (pair.length < 2) return acc
        const key = pair[0];
        const val = pair[1];
        const matchingFilterObject = availableFilters.find(i => i.key === key);

        if (key) {
            if (matchingFilterObject) {
                /**
                 *  Handle filter related values - will always return an array
                 */

                if (val.length) {
                    acc[key] = val.split(matchingFilterObject.delimiter || ",").map(i => {
                        if (matchingFilterObject.int) {
                            return parseInt(i);
                        } else {
                            return decodeURI(i);
                        }
                    });
                } else {
                    acc[key] = [];
                }
            }
            else if (TABLE_OPTIONS_KEYS.includes(key)) {
                /**
                 *  Key is a v-data-table options key
                 *  v-data-table is strict on types and will go nuts if types are wrong.
                 */

                if (["page", "itemsPerPage"].includes(key)) {
                    acc[key] = parseInt(val);
                } else if (["sortDesc", "mustSort", "multiSort"].includes(key)) {
                    acc[key] = val === "true";
                } else if (["sortBy"].includes(key)) {
                    acc[key] = val;
                }
                // fixme :: are all "use cases" handled above ?

            }
            else if (val.includes(',')) {
                /**
                 * Value is probably a non-filter csv
                 */

                const arr = val.split(",");

                if (arr.length === 1 && arr[0] === "") {
                    acc[key] = [];
                } else {
                    acc[key] = arr.map(i => decodeURIComponent(i));
                }

            } else {
                /**
                 * Value is probably a boolean
                 */

                if (['true', 'false'].includes(val)) {
                    acc[key] = val === 'true';
                } else {
                    acc[key] = decodeURIComponent(val);
                }
            }
        }

        return acc;
    }, {});
    return qq
}

/**
 * Get a fallback value for a type
 */
export const getTypeDefault = function (obj) {
    function fallbackFunc(x) {
        return x
    }

    let type = toType(obj)
    let values = {
        undefined: undefined,
        null: null,
        string: '',
        object: {},
        array: [],
        number: null,
        boolean: false,
        date: new Date(),
        function: fallbackFunc
    }
    return values[type]
}